/**
 * ScannerPanel - Progressive scan results display
 * Shows scan progress and reveals specimen data incrementally
 *
 * Enhanced with bioluminescent deep-sea aesthetic
 */

import Phaser from 'phaser';
import type { ScannerSystem, ScanProgressEvent, ScanCompleteEvent, PartialScanResult } from '@/game/systems';
import type { LifeFunction } from '@/types';
import { COLORS, TIMING, createGlowingPanel, createGlowingProgressBar, createDataRow, revealAnimation } from '@/ui/theme/BioluminescentTheme';

export interface ScannerPanelConfig {
  /** Position X */
  x?: number;
  /** Position Y */
  y?: number;
  /** Width of the panel */
  width?: number;
}

interface DataRowState {
  container: Phaser.GameObjects.Container;
  labelText: Phaser.GameObjects.Text;
  valueText: Phaser.GameObjects.Text;
  indicator?: Phaser.GameObjects.Graphics;
  setValue: (value: string, revealed?: boolean) => void;
  revealed: boolean;
}

/**
 * ScannerPanel displays progressive scan results
 * Enhanced with bioluminescent deep-sea aesthetic
 */
export class ScannerPanel {
  private scene: Phaser.Scene;
  private config: Required<ScannerPanelConfig>;

  /** Main container */
  private container!: Phaser.GameObjects.Container;

  /** UI elements */
  private progressBarObj!: ReturnType<typeof createGlowingProgressBar>;
  private progressText!: Phaser.GameObjects.Text;
  private dataRows: Map<string, DataRowState> = new Map();
  private titleText!: Phaser.GameObjects.Text;
  private scanningIndicator!: Phaser.GameObjects.Graphics;
  private scanningTween?: Phaser.Tweens.Tween;
  private pulseParticles: Phaser.GameObjects.Graphics[] = [];

  /** Data row keys */
  private static readonly DATA_KEYS = ['cellCount', 'cellType', 'isLiving', 'functions'] as const;

  constructor(
    scene: Phaser.Scene,
    _scanner: ScannerSystem,
    config: ScannerPanelConfig = {}
  ) {
    this.scene = scene;

    const camera = scene.cameras.main;
    this.config = {
      x: config.x ?? 20,
      y: config.y ?? camera.height - 210,
      width: config.width ?? 280,
    };

    this.create();
    this.setupEventListeners();
    this.setVisible(false);
  }

  /**
   * Create all panel elements
   */
  private create(): void {
    this.container = this.scene.add.container(this.config.x, this.config.y);
    this.container.setDepth(1000);

    this.createPanel();
    this.createTitle();
    this.createProgressBar();
    this.createDataRows();
    this.createAmbientEffects();
  }

  /**
   * Create the panel background using theme
   */
  private createPanel(): void {
    const { width } = this.config;
    const height = 180;

    const panel = createGlowingPanel(this.scene, width, height, {
      glowColor: COLORS.cyanGlow,
      glowIntensity: 0.25,
      cornerRadius: 14,
      borderWidth: 2,
      hasInnerGlow: true,
      hasCornerAccents: true,
      hasScanlines: true,
    });

    this.container.add(panel);
  }

  /**
   * Create the title with scanning indicator
   */
  private createTitle(): void {
    // Scanning indicator (animated dot)
    this.scanningIndicator = this.scene.add.graphics();
    this.scanningIndicator.setPosition(16, 18);
    this.drawScanningIndicator(1);
    this.container.add(this.scanningIndicator);

    // Title text
    this.titleText = this.scene.add.text(30, 12, 'ANALYZING SPECIMEN', {
      fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
      fontSize: '11px',
      color: '#00d4ff',
      fontStyle: 'bold',
      letterSpacing: 1,
    });
    this.container.add(this.titleText);

    // Animated scanning indicator
    this.scanningTween = this.scene.tweens.add({
      targets: { alpha: 0.3 },
      alpha: 1,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        this.drawScanningIndicator(tween.getValue() ?? 0.3);
      },
    });

    // Decorative line under title
    const titleLine = this.scene.add.graphics();
    titleLine.lineStyle(1, COLORS.cyanGlow, 0.4);
    titleLine.lineBetween(16, 30, this.config.width - 16, 30);
    this.container.add(titleLine);
  }

  /**
   * Draw the scanning indicator dot
   */
  private drawScanningIndicator(alpha: number): void {
    this.scanningIndicator.clear();
    // Outer glow
    this.scanningIndicator.fillStyle(COLORS.cyanGlow, alpha * 0.3);
    this.scanningIndicator.fillCircle(0, 0, 6);
    // Core
    this.scanningIndicator.fillStyle(COLORS.cyanGlow, alpha);
    this.scanningIndicator.fillCircle(0, 0, 3);
  }

  /**
   * Create the progress bar using theme
   */
  private createProgressBar(): void {
    const barWidth = this.config.width - 80;
    const x = 16;
    const y = 42;

    this.progressBarObj = createGlowingProgressBar(this.scene, barWidth, 10, {
      trackColor: COLORS.dataHidden,
      fillColor: COLORS.success,
      glowColor: COLORS.greenGlow,
    });

    this.progressBarObj.container.setPosition(x, y);
    this.container.add(this.progressBarObj.container);

    // Progress percentage text with glow effect
    this.progressText = this.scene.add.text(x + barWidth + 12, y - 1, '0%', {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: '11px',
      color: '#10b981',
      fontStyle: 'bold',
    });
    this.container.add(this.progressText);
  }

  /**
   * Create data rows using theme components
   */
  private createDataRows(): void {
    const startY = 65;
    const rowHeight = 26;
    const rowWidth = this.config.width - 32;

    const labels: Record<string, string> = {
      cellCount: 'Cell Count',
      cellType: 'Cell Type',
      isLiving: 'Living Status',
      functions: 'Life Functions',
    };

    ScannerPanel.DATA_KEYS.forEach((key, index) => {
      const y = startY + index * rowHeight;

      const row = createDataRow(this.scene, labels[key], rowWidth, {
        labelColor: '#94d2ff',
        valueColor: '#3a5a7a',
        fontSize: 11,
        hasIndicator: true,
        indicatorColor: COLORS.cyanGlow,
      });

      row.container.setPosition(16, y);
      this.container.add(row.container);

      this.dataRows.set(key, {
        ...row,
        revealed: false,
      });
    });
  }

  /**
   * Create ambient floating particles for atmosphere
   */
  private createAmbientEffects(): void {
    // Create a few floating particle dots
    for (let i = 0; i < 4; i++) {
      const particle = this.scene.add.graphics();
      const x = 20 + Math.random() * (this.config.width - 40);
      const y = 60 + Math.random() * 100;

      particle.fillStyle(COLORS.cyanGlow, 0.2);
      particle.fillCircle(0, 0, 2);
      particle.setPosition(x, y);
      this.container.add(particle);
      this.pulseParticles.push(particle);

      // Floating animation
      this.scene.tweens.add({
        targets: particle,
        y: y - 10 - Math.random() * 10,
        alpha: { from: 0.2, to: 0.5 },
        duration: 2000 + Math.random() * 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Math.random() * 1000,
      });
    }
  }

  /**
   * Set up event listeners
   */
  private setupEventListeners(): void {
    this.scene.events.on('scanner:activated', this.handleActivated, this);
    this.scene.events.on('scanner:deactivated', this.handleDeactivated, this);
    this.scene.events.on('scanner:progress', this.handleProgress, this);
    this.scene.events.on('scanner:complete', this.handleComplete, this);
  }

  /**
   * Handle scanner activated
   */
  private handleActivated(): void {
    this.reset();
    this.setVisible(true);

    // Animate panel entrance
    this.container.setAlpha(0);
    this.container.setScale(0.95);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: TIMING.normal,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Handle scanner deactivated
   */
  private handleDeactivated(): void {
    // Animate panel exit
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      scaleX: 0.95,
      scaleY: 0.95,
      duration: TIMING.fast,
      ease: 'Power2',
      onComplete: () => {
        this.setVisible(false);
        this.container.setAlpha(1);
        this.container.setScale(1);
      },
    });
  }

  /**
   * Handle scan progress
   */
  private handleProgress(event: ScanProgressEvent): void {
    this.setVisible(true);

    // Update progress bar with theme component
    const progress = event.percent / 100;
    this.progressBarObj.setProgress(progress);
    this.progressText.setText(`${Math.round(event.percent)}%`);

    // Update revealed data
    this.updateRevealedData(event.partialResult);
  }

  /**
   * Handle scan complete
   */
  private handleComplete(event: ScanCompleteEvent): void {
    // Stop scanning animation
    if (this.scanningTween) {
      this.scanningTween.stop();
    }

    // Update title with success state
    this.titleText.setText('ANALYSIS COMPLETE');
    this.titleText.setColor('#10b981');

    // Update scanning indicator to success
    this.scanningIndicator.clear();
    this.scanningIndicator.fillStyle(COLORS.success, 0.4);
    this.scanningIndicator.fillCircle(0, 0, 6);
    this.scanningIndicator.fillStyle(COLORS.success, 1);
    this.scanningIndicator.fillCircle(0, 0, 3);

    this.progressBarObj.setProgress(1);
    this.progressText.setText('100%');

    // Reveal all data
    this.revealAllData(event.result);

    // Flash effect
    this.flashComplete();
  }

  /**
   * Update revealed data based on partial result
   */
  private updateRevealedData(result: PartialScanResult): void {
    if (result.cellCount !== undefined) {
      this.revealRow('cellCount', this.formatCellCount(result.cellCount));
    }

    if (result.cellType !== undefined) {
      this.revealRow('cellType', this.formatCellType(result.cellType));
    }

    if (result.isLiving !== undefined) {
      this.revealRow('isLiving', result.isLiving ? 'Yes' : 'No');
    }

    if (result.detectedFunctions !== undefined && result.detectedFunctions.length > 0) {
      this.revealRow('functions', this.formatFunctions(result.detectedFunctions));
    }
  }

  /**
   * Reveal all data from complete result
   */
  private revealAllData(result: { cellCount: unknown; cellType: string; isLiving: boolean; detectedFunctions: LifeFunction[] }): void {
    this.revealRow('cellCount', this.formatCellCount(result.cellCount));
    this.revealRow('cellType', this.formatCellType(result.cellType));
    this.revealRow('isLiving', result.isLiving ? 'Yes' : 'No');
    this.revealRow('functions', this.formatFunctions(result.detectedFunctions));
  }

  /**
   * Reveal a single data row with animation
   */
  private revealRow(key: string, text: string): void {
    const row = this.dataRows.get(key);
    if (!row || row.revealed) return;

    row.setValue(text, true);
    row.revealed = true;

    // Use theme reveal animation on the value text
    revealAnimation(this.scene, row.valueText, {
      duration: TIMING.normal,
    });
  }

  /**
   * Format cell count for display
   */
  private formatCellCount(count: unknown): string {
    if (count === 'none') return 'None';
    if (count === 'single') return 'Single';
    if (count === 'multiple') return 'Multiple';
    if (typeof count === 'number') return count.toString();
    return 'Unknown';
  }

  /**
   * Format cell type for display
   */
  private formatCellType(type: string): string {
    switch (type) {
      case 'prokaryotic':
        return 'Prokaryotic';
      case 'eukaryotic':
        return 'Eukaryotic';
      case 'eukaryotic-plant':
        return 'Eukaryotic (Plant)';
      case 'none':
        return 'None';
      default:
        return type;
    }
  }

  /**
   * Format life functions for display
   */
  private formatFunctions(functions: LifeFunction[]): string {
    if (functions.length === 0) return 'None';
    if (functions.length <= 2) {
      return functions.map(f => this.formatFunctionName(f)).join(', ');
    }
    return `${functions.length} detected`;
  }

  /**
   * Format a single function name
   */
  private formatFunctionName(func: LifeFunction): string {
    const name = func.toString().toLowerCase().replace(/_/g, ' ');
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  /**
   * Flash effect on complete with enhanced glow
   */
  private flashComplete(): void {
    const flash = this.scene.add.graphics();

    // Multiple glow layers for richer effect
    flash.fillStyle(COLORS.greenGlow, 0.15);
    flash.fillRoundedRect(-8, -8, this.config.width + 16, 196, 18);
    flash.fillStyle(COLORS.greenGlow, 0.25);
    flash.fillRoundedRect(-4, -4, this.config.width + 8, 188, 16);
    flash.fillStyle(COLORS.success, 0.3);
    flash.fillRoundedRect(0, 0, this.config.width, 180, 14);

    this.container.add(flash);
    this.container.sendToBack(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: TIMING.gentle,
      ease: 'Power2',
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Reset panel to initial state
   */
  public reset(): void {
    this.titleText.setText('ANALYZING SPECIMEN');
    this.titleText.setColor('#00d4ff');

    // Reset scanning indicator animation
    if (this.scanningTween) {
      this.scanningTween.stop();
    }
    this.scanningTween = this.scene.tweens.add({
      targets: { alpha: 0.3 },
      alpha: 1,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        this.drawScanningIndicator(tween.getValue() ?? 0.3);
      },
    });

    this.progressBarObj.setProgress(0);
    this.progressText.setText('0%');

    // Reset all data rows
    this.dataRows.forEach((row) => {
      row.setValue('???', false);
      row.revealed = false;
    });
  }

  /**
   * Set visibility
   */
  public setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Set position
   */
  public setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Get the container
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Clean up
   */
  public destroy(): void {
    // Stop tweens
    if (this.scanningTween) {
      this.scanningTween.stop();
    }

    // Clean up particles
    this.pulseParticles.forEach(p => p.destroy());
    this.pulseParticles = [];

    // Remove event listeners
    this.scene.events.off('scanner:activated', this.handleActivated, this);
    this.scene.events.off('scanner:deactivated', this.handleDeactivated, this);
    this.scene.events.off('scanner:progress', this.handleProgress, this);
    this.scene.events.off('scanner:complete', this.handleComplete, this);

    this.container.destroy();
  }
}
